import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Chip,
  Box,
  CircularProgress,
  TextField,
  Tabs,
  Tab,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useApi } from "../../utils/api";
import { useUserInfo } from "../../utils/user";

const TAB_ASSIGN = 0;
const TAB_PURCHASED = 1;

const ManageProductsDialog = ({ open, onClose, client }) => {
  const {
    getProducts,
    getUserProducts,
    postUserProducts,
    patchUserProduct,
    getSubscription,
    cancelSubscription,
    uncancelSubscription,
    updateSubscriptionTrialEnd,
  } = useApi();
  const { stripeAccountId } = useUserInfo();
  const [tab, setTab] = useState(TAB_ASSIGN);
  const [products, setProducts] = useState([]);
  const [assignedProducts, setAssignedProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [clientPurchasedProducts, setClientPurchasedProducts] = useState([]);
  const [quantityValues, setQuantityValues] = useState({});
  const [clientSubscription, setClientSubscription] = useState(null);
  const [subscriptionActionLoading, setSubscriptionActionLoading] =
    useState(false);
  const [trialEndDate, setTrialEndDate] = useState("");

  const loadProductsData = async () => {
    if (!client) return;
    setProductsLoading(true);
    try {
      const promises = [
        getProducts(stripeAccountId),
        getUserProducts(client.id, "assigned"),
        getUserProducts(client.id, "purchased"),
      ];
      if (client.stripeCustomerId) {
        promises.push(
          getSubscription(client.stripeCustomerId, stripeAccountId)
        );
      }
      const results = await Promise.all(promises);
      const [allProducts, clientProducts, clientPurchasedProducts] =
        results.slice(0, 3);
      setProducts(allProducts);
      setAssignedProducts(clientProducts);
      setClientPurchasedProducts(clientPurchasedProducts);
      const purchasedPriceIds = new Set(
        (clientPurchasedProducts || []).map((p) => p.priceId)
      );
      const assignedPriceIds = (clientProducts || [])
        .filter((p) => !purchasedPriceIds.has(p.priceId))
        .map((p) => p.priceId);
      setSelectedProducts(assignedPriceIds);
      if (results[3] !== undefined) {
        const sub = results[3];
        setClientSubscription(sub && sub.id ? sub : null);
      } else {
        setClientSubscription(null);
      }
    } catch (error) {
      console.error("Failed to load products:", error);
      setClientSubscription(null);
    } finally {
      setProductsLoading(false);
    }
  };

  useEffect(() => {
    if (open && client) {
      setTab(TAB_ASSIGN);
      loadProductsData();
    }
  }, [open, client]);

  const handleAssignProducts = async () => {
    try {
      const items = selectedProducts.map((priceId) => {
        const product = products.find((p) =>
          p.prices.some((price) => price.priceId === priceId)
        );
        return {
          productId: product.productId,
          priceId: priceId,
        };
      });

      await postUserProducts({
        items,
        type: "assigned",
        userId: client.id,
      });
      onClose();
    } catch (error) {
      console.error("Failed to assign products:", error);
      alert("Failed to assign products. Please try again.");
    }
  };

  const handleQuantityChange = async (priceId, newValue) => {
    try {
      await patchUserProduct({
        userId: client.id,
        priceId,
        action: "updateQuantity",
        value: parseInt(newValue),
      });

      setClientPurchasedProducts((prev) =>
        prev.map((product) =>
          product.priceId === priceId
            ? { ...product, quantityLeft: parseInt(newValue) }
            : product
        )
      );

      setQuantityValues((prev) => ({
        ...prev,
        [priceId]: newValue,
      }));
    } catch (error) {
      console.error("Failed to update quantity:", error);
      alert("Failed to update quantity. Please try again.");
    }
  };

  const handleCancelSubscription = async () => {
    if (!clientSubscription?.id) return;
    setSubscriptionActionLoading(true);
    try {
      const updated = await cancelSubscription(
        clientSubscription.id,
        stripeAccountId
      );
      setClientSubscription(updated);
    } catch (error) {
      console.error("Failed to cancel subscription:", error);
      alert("Failed to cancel subscription. Please try again.");
    }
    setSubscriptionActionLoading(false);
  };

  const handleUncancelSubscription = async () => {
    if (!clientSubscription?.id) return;
    setSubscriptionActionLoading(true);
    try {
      const updated = await uncancelSubscription(
        clientSubscription.id,
        stripeAccountId
      );
      setClientSubscription(updated);
    } catch (error) {
      console.error("Failed to reinstate subscription:", error);
      alert("Failed to reinstate subscription. Please try again.");
    }
    setSubscriptionActionLoading(false);
  };

  const handleExtendTrialEnd = async () => {
    if (!clientSubscription?.id || !trialEndDate) return;
    const trialEndUnix = Math.floor(
      new Date(trialEndDate + "T23:59:59").getTime() / 1000
    );
    setSubscriptionActionLoading(true);
    try {
      const updated = await updateSubscriptionTrialEnd(
        clientSubscription.id,
        trialEndUnix,
        stripeAccountId
      );
      setClientSubscription(updated);
      setTrialEndDate("");
    } catch (error) {
      console.error("Failed to extend billing date:", error);
      alert("Failed to extend billing date. Please try again.");
    }
    setSubscriptionActionLoading(false);
  };

  const purchasedOneTime = clientPurchasedProducts.filter(
    (p) => p.recurring === false
  );
  const purchasedRecurring = clientPurchasedProducts.filter(
    (p) => p.recurring === true
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Manage Products for {client?.name}</DialogTitle>
      <DialogContent>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
        >
          <Tab label="Assign products" />
          <Tab label="Purchased" />
        </Tabs>

        {productsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : tab === TAB_ASSIGN ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Assign</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((product) =>
                  product.prices
                    .filter(
                      (price) =>
                        !clientPurchasedProducts.some(
                          (up) => up.priceId === price.priceId
                        )
                    )
                    .map((price) => {
                      const isAssigned = assignedProducts.some(
                        (up) => up.priceId === price.priceId
                      );

                      return (
                        <TableRow key={price.priceId}>
                          <TableCell>{product.name}</TableCell>
                          <TableCell>{price.metadata.description}</TableCell>
                          <TableCell align="right">
                            ${(price.unitAmount / 100).toFixed(2)}
                          </TableCell>
                          <TableCell align="center">
                            {isAssigned ? (
                              <Chip
                                label="Assigned"
                                color="primary"
                                size="small"
                              />
                            ) : (
                              <Chip
                                label="Not Active"
                                color="default"
                                size="small"
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Checkbox
                              checked={
                                isAssigned ||
                                selectedProducts.includes(price.priceId)
                              }
                              onChange={() => {
                                setSelectedProducts((prev) =>
                                  prev.includes(price.priceId)
                                    ? prev.filter((id) => id !== price.priceId)
                                    : [...prev, price.priceId]
                                );
                              }}
                              disabled={isAssigned}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ pt: 1 }}>
            {clientPurchasedProducts.length === 0 ? (
              <Typography color="text.secondary">
                No purchased products yet.
              </Typography>
            ) : (
              <>
                {purchasedOneTime.length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      One-time purchases
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Product</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell align="right">Price</TableCell>
                            <TableCell align="center">Quantity</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {purchasedOneTime.map((p) => (
                            <TableRow key={p.priceId}>
                              <TableCell>{p.productName}</TableCell>
                              <TableCell>{p.priceDescription}</TableCell>
                              <TableCell align="right">
                                ${(p.price ?? 0).toFixed(2)}
                              </TableCell>
                              <TableCell align="center">
                                <TextField
                                  type="number"
                                  size="small"
                                  value={
                                    quantityValues[p.priceId] ??
                                    p.quantityLeft ??
                                    0
                                  }
                                  onChange={(e) =>
                                    setQuantityValues((prev) => ({
                                      ...prev,
                                      [p.priceId]: e.target.value,
                                    }))
                                  }
                                  onBlur={(e) =>
                                    handleQuantityChange(
                                      p.priceId,
                                      e.target.value
                                    )
                                  }
                                  inputProps={{ min: 0 }}
                                  sx={{ width: "80px" }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}

                {purchasedRecurring.length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                      Recurring subscriptions
                    </Typography>
                    {!client?.stripeCustomerId ? (
                      <Typography color="text.secondary" variant="body2">
                        Client has no payment profile; subscription actions are
                        not available.
                      </Typography>
                    ) : !clientSubscription ? (
                      <Typography color="text.secondary" variant="body2">
                        No active subscription found for this client.
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.5,
                        }}
                      >
                        {purchasedRecurring.map((p) => (
                          <Box
                            key={p.priceId}
                            sx={{
                              py: 1,
                              px: 1.5,
                              bgcolor: "action.hover",
                              borderRadius: 1,
                            }}
                          >
                            <Typography>
                              {p.productName} — ${(p.price ?? 0).toFixed(2)}
                              {p.priceDescription && ` · ${p.priceDescription}`}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                alignItems: "center",
                                gap: 1,
                                mt: 1,
                              }}
                            >
                              {clientSubscription.cancel_at_period_end ? (
                                <LoadingButton
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  onClick={handleUncancelSubscription}
                                  loading={subscriptionActionLoading}
                                >
                                  Reinstate subscription
                                </LoadingButton>
                              ) : (
                                <LoadingButton
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  onClick={handleCancelSubscription}
                                  loading={subscriptionActionLoading}
                                >
                                  Cancel at period end
                                </LoadingButton>
                              )}
                              <TextField
                                size="small"
                                type="date"
                                label="Extend billing to"
                                value={trialEndDate}
                                onChange={(e) =>
                                  setTrialEndDate(e.target.value)
                                }
                                InputLabelProps={{ shrink: true }}
                                sx={{ width: 180 }}
                              />
                              <LoadingButton
                                size="small"
                                variant="contained"
                                disabled={!trialEndDate}
                                onClick={handleExtendTrialEnd}
                                loading={subscriptionActionLoading}
                              >
                                Extend
                              </LoadingButton>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {tab === TAB_ASSIGN && (
          <Button
            onClick={handleAssignProducts}
            variant="contained"
            color="primary"
          >
            Save Changes
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ManageProductsDialog;
